
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

const tiers = [
  {
    name: "Free",
    price: 0,
    queueLimit: 1,
    description: "Perfect for trying out the platform",
    features: [
      "No credit card needed",
      "1 queue",
      "Real-time updates",
      "Basic analytics",
      "Valid for 3 days"
    ],
  },
  {
    name: "Basic",
    price: 15,
    queueLimit: 3,
    description: "Perfect for small businesses",
    features: [
      "Up to 3 queues",
      "Real-time updates",
      "Email support",
      "Analytics",
    ],
  },
  {
    name: "Pro",
    price: 19,
    queueLimit: 5,
    description: "Ideal for growing businesses",
    features: [
      "Up to 5 queues",
    ],
  },
  {
    name: "Enterprise",
    price: 30,
    queueLimit: 10,
    description: "Best for large organizations",
    features: [
      "Up to 10 queues",
    ],
  },
];

export const PricingSection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-16 px-4 bg-secondary/30">
      <div className="container mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Simple, Transparent Pricing
          </h2>
          <p className="text-muted-foreground">
            Choose the plan that best fits your business needs
          </p>
        </motion.div>

        <div className="grid md:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {tiers.map((tier, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
            >
              <Card className="p-6 flex flex-col h-full border-border/50 hover:border-accent/50 transition-colors">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-primary">{tier.name}</h3>
                  <p className="text-muted-foreground">{tier.description}</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-bold">${tier.price}</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <ul className="space-y-3 mb-8 flex-grow">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-accent" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant={index === 1 ? "default" : "outline"}
                  className="w-full"
                  onClick={() => navigate("/login")}
                >
                  {tier.price === 0 ? "Start Free" : "Get Started"}
                </Button>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
